import {
  RECOMMENDATIONS_SHELF_TYPE,
  CATEGORY_SHELF_TYPE,
  CATEGORY_SAVINGS_SHELF_TYPE,
  BUY_AGAIN_SAVINGS_SHELF_TYPE,
  FEATURED_PROMOTIONS_SHELF_TYPE,
  DISCOVERY_PRODUCTS_SHELF_TYPE,
  DID_YOU_FORGET_SHELF_TYPE,
  CAMPAIGN_PROMOS_SHELF_TYPE,
  SHELF_RENDER_TYPE,
  TRENDING_ITEMS_SHELF_TYPE,
  BUY_AGAIN_SINGLE_RETAILER_TYPE,
} from '@/features/shared/constants/shelves'
import { type Shelf, type ShelfAPI } from '@/features/shop/services/Shelves/types'
import { type Coupon } from '@/features/shop/services/Coupons/types'
import { PromotionTypes } from '@/features/shop/services/Campaigns/types'
import { type ProductCampaignsPromotion } from '@/features/shop/services/Product/types'
import { normalizeProduct } from '@/features/shared/utils/normalizeProduct'
import { normalizeCoupon } from '@/features/shop/utils/normalizeCoupon'

export const normalizeShelf = (shelf: ShelfAPI): Shelf => {
  const { metadata, name } = shelf

  const shelfProps: Shelf = {
    name: name ?? '',
    items: shelf.items ?? [],
    type: shelf.type ?? '',
    total: shelf.total ?? 0,
    metadata: metadata
      ? {
          ...metadata,
          ...(metadata?.coupons && {
            coupons: metadata.coupons.map(normalizeCoupon),
          }),
        }
      : null,
    elapsed: shelf.elapsed ?? '',
    renderType: null,
    see_more: shelf.see_more ?? false,
  }

  switch (shelf.type) {
    case FEATURED_PROMOTIONS_SHELF_TYPE:
      return {
        ...shelfProps,
        items: shelfProps.items
          // Since promotional shelves are generally associated with promotional
          // progress bars, we need to make sure the progress is fetched for the
          // correct promotion. We filter the products' [potentially multiple]
          // promotions to ensure the correct promotional category id is used to
          // fetch progress. This is another symptom of having products that can
          // belong to multiple promotions.
          .map((item) => {
            return {
              ...item,
              cpg_promotions: item?.cpg_promotions?.filter(
                (promotion: ProductCampaignsPromotion) =>
                  promotion.category_id === shelf.metadata.category_id,
              ),
            }
          })
          .map((product) => normalizeProduct(product)),
        renderType: SHELF_RENDER_TYPE.FEATURED_PROMOTIONS,
      }
    case RECOMMENDATIONS_SHELF_TYPE:
    case CATEGORY_SHELF_TYPE:
    case CATEGORY_SAVINGS_SHELF_TYPE:
    case BUY_AGAIN_SAVINGS_SHELF_TYPE:
    case DISCOVERY_PRODUCTS_SHELF_TYPE:
    case DID_YOU_FORGET_SHELF_TYPE:
    case TRENDING_ITEMS_SHELF_TYPE:
      return {
        ...shelfProps,
        items: shelfProps.items.map((product) => normalizeProduct(product)),
        renderType: SHELF_RENDER_TYPE.PRODUCT,
      }
    case CAMPAIGN_PROMOS_SHELF_TYPE:
      return {
        ...shelfProps,
        items: shelfProps.items.filter((promo) => {
          return Object.values(PromotionTypes).includes(promo.type)
        }),
        renderType: SHELF_RENDER_TYPE.CAMPAIGN_PROMOS,
      }
    case BUY_AGAIN_SINGLE_RETAILER_TYPE:
      return {
        ...shelfProps,
        items: shelfProps.items.map((product) => normalizeProduct(product)),
        renderType: SHELF_RENDER_TYPE.BUY_AGAIN_SINGLE_RETAILER,
        row_position: 0,
      }
    default:
      return shelfProps
  }
}

export const getCouponsFromShelves = (shelves: Shelf<Coupon | null>[]): Coupon[] =>
  shelves.reduce<Coupon[]>((acc, shelf) => {
    const coupons = shelf.metadata?.coupons
    if (coupons?.length) acc = [...acc, ...coupons]
    return acc
  }, [])
